import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperButtonComponent, PaperIconComponent } from '@ricoh-paper/angular';
import { AppUpdateService } from '../../../../core/services/app-update.service';

/**
 * Floating "update available" pill for BARE routes.
 *
 * The employee shell surfaces new deploys through the top banner stack in
 * `AppShellComponent`, but bare routes (/admin, /vendor, the previews)
 * bypass that shell entirely — so a user who lives in the admin area all day
 * never learnt a new build had shipped and eventually hit a stale chunk map.
 * This pill is the bare-route equivalent: it self-gates on
 * `AppUpdateService.updateAvailable()` and offers the same one-click reload
 * onto the fresh `index.html` + chunk map.
 *
 * MOUNTED: once, alongside the bare-route `<router-outlet/>` in `app.ts`
 * (except signage, where unattended displays auto-reload instead — see the
 * auto-apply effect in `app.ts`). Inert until an update is detected.
 *
 * ACCESSIBILITY: non-modal `role="status"` + polite live region — it is an
 * invitation, never a focus trap. The refresh action is a labelled button.
 */
@Component({
  selector: 'im-app-update-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, PaperButtonComponent, PaperIconComponent],
  templateUrl: './app-update-pill.component.html',
  styleUrl: './app-update-pill.component.scss',
  host: {
    // This surface fires its own typed APP_UPDATE_APPLIED event.
    class: 'ph-no-capture',
  },
})
export class AppUpdatePillComponent {
  protected readonly appUpdate = inject(AppUpdateService);

  /** Reload onto the freshly deployed build. */
  protected refresh(): void {
    void this.appUpdate.applyUpdate('pill');
  }
}
